import React, { useEffect, useMemo, useState } from "react";
import { Box, CircularProgress, Button, TextField } from "@mui/material";
import Typography from "@mui/material/Typography";
import Swal from "sweetalert2";
import { useDispatch, useSelector } from "react-redux";

import { setProducts, addProduct, deleteProduct, editProduct } from "../../../features/productsSlice";
import { GetProducts, RemoveProduct, GetProductOptions } from "../../../utils/product";
import { GetCategories } from "../../../utils/categoryAPI";

import ProductsToolbar from "./ProductsToolbar";
import ProductsTable from "./ProductsTable";
import ProductFormDialog from "./ProductFormDialog";

import { getAncestors, resolveImgSrc } from "./helpers/productForm.utils";

export default function AdminProducts() {
  const products = useSelector((state) => state.product.products);
  const dispatch = useDispatch();

  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [categories, setCategories] = useState([]);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const pageSize = 24;
  const [loading, setLoading] = useState(false);
  const [debouncedSearch, setDebouncedSearch] = useState("");




  const productEmpty = useMemo(
    () => ({
      name: "",
      price: 0,
      quantity: 100000,
      parentCategoryId: 0,
      categoryId: 0,
      subCategoryId: 0,
      categoryIds: [],
      description: "",
      image: null,
      id: 0,
      variationType: "",
      options: [{ id: 0, value: "", extraPrice: 0, quantity: 0, image: null }],
    }),
    []
  );

  const [initialValues, setInitialValues] = useState(productEmpty);


  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);


  // load categories once
  useEffect(() => {
    (async () => {
      try {
        const data = await GetCategories();
        setCategories(Array.isArray(data) ? data : []);
      } catch (e) {
        console.error("Failed to load categories", e);
        setCategories([]);
      }
    })();
  }, []);

  // load products
  useEffect(() => {
    let mounted = true;

    (async () => {
      try {
        setLoading(true); // ✅ כאן מתחילה טעינה

        const data = await GetProducts({
          page: currentPage,
          pageSize,
          searchTerm: debouncedSearch?.trim() || undefined,

        });

        const items = Array.isArray(data?.items)
          ? data.items
          : Array.isArray(data?.Items)
            ? data.Items
            : [];

        if (!mounted) return;

        dispatch(setProducts(items));
        setTotalPages(data?.totalPages ?? data?.TotalPages ?? 1);
      } catch (e) {
        console.error("Failed to load products", e);
        if (!mounted) return;
        dispatch(setProducts([]));
        setTotalPages(1);
      } finally {
        if (mounted) setLoading(false); // ✅ כאן מסתיימת טעינה
      }
    })();

    return () => {
      mounted = false;
    };
  }, [dispatch, currentPage, pageSize, debouncedSearch]);





  const filteredProducts = products; // כי כבר מסונן מהשרת

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch]);



  const handleAddClick = () => {
    setInitialValues(productEmpty);
    setOpen(true);
  };

  const handleCloseDialog = () => setOpen(false);

  const handelDeletProduct = async (id) => {
    const result = await Swal.fire({
      title: "בטוח למחוק מוצר מהרשימה ?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "כן, מחק מוצר מהרשימה!",
    });

    if (!result.isConfirmed) return;

    try {
      const res = await RemoveProduct(id);
      if (res === true) {
        dispatch(deleteProduct(id));
        Swal.fire({ title: "בוצע בהצלחה", text: "המוצר נמחק!", icon: "success", timer: 1500, showConfirmButton: false });
      }
    } catch (err) {
      console.error(err);
      Swal.fire({ title: "מחיקה נכשלה", icon: "error" });
    }
  };

  const handelProductEdit = async (product) => {
    // const { parentId, childId, subId } = getAncestors(categories, product.categoryId);

    // load options
    let opts = [];
    try {
      opts = await GetProductOptions(product.id);
    } catch (e) {
      console.error("Failed to load product options", e);
      opts = [];
    }

    const variationType = opts.length ? (opts[0].OptionType ?? opts[0].optionType ?? "") : "";
    const formOptions = opts.length
      ? opts.map((o) => ({
        id: o.Id ?? o.id ?? 0,
        value: o.Value ?? o.value ?? "",
        extraPrice: o.ExtraPrice ?? o.extraPrice ?? 0,
        quantity: o.Quantity ?? o.quantity ?? 0,
        image: o.Image ?? o.image ?? null,
      }))
      : [{ id: 0, value: "", extraPrice: 0, quantity: 0 }];

setInitialValues({
  ...product,
  description: product.description ?? "",
  parentCategoryId: 0,
  categoryId: 0,
  subCategoryId: 0,
  categoryIds: product.categoryIds ?? [product.categoryId],
  variationType,
  options: formOptions,
});

    setOpen(true);
  };

  const handleSaved = ({ mode, product }) => {
    if (!product) return;
    if (mode === "add") dispatch(addProduct(product));
    if (mode === "edit") dispatch(editProduct(product));
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" sx={{ p: 4 }}>
        <CircularProgress size={30} sx={{ mr: 2 }} />
        <Typography variant="h5">טוען מוצרים...</Typography>
      </Box>
    );
  }

  if (!loading && Array.isArray(products) && products.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" sx={{ p: 4 }}>
        <Typography variant="h5">לא נמצאו מוצרים</Typography>
      </Box>
    );
  }


  return (
    <>
      <Typography sx={{ md: 1 }} variant="h4">
        מוצרים
      </Typography>

      <ProductsToolbar
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        onAddClick={handleAddClick}
      />

      <ProductsTable
        products={filteredProducts}
        categories={categories}
        onEdit={handelProductEdit}
        onDelete={handelDeletProduct}
        resolveImgSrc={resolveImgSrc}
      />

      <ProductFormDialog
        open={open}
        onClose={handleCloseDialog}
        initialValues={initialValues}
        categories={categories}
        onSaved={handleSaved}
      />
      <Box display="flex" justifyContent="center" alignItems="center" sx={{ mt: 4, gap: 2 }}>
        <Button
          variant="outlined"
          disabled={currentPage <= 1}
          onClick={() => setCurrentPage(p => p - 1)}
        >
          הקודם
        </Button>

        <Box display="flex" alignItems="center" gap={1}>
          <Typography>עמוד</Typography>

          <TextField
            size="small"
            type="number"
            value={currentPage}
            inputProps={{ min: 1, max: totalPages }}
            sx={{
              width: 45,

              "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button": {
                WebkitAppearance: "none",
                margin: 0,
              },

              "& input[type=number]": {
                MozAppearance: "textfield",
              },
            }}
            onChange={(e) => {
              const value = Number(e.target.value);

              if (!value) return;

              if (value < 1) {
                setCurrentPage(1);
              } else if (value > totalPages) {
                setCurrentPage(totalPages);
              } else {
                setCurrentPage(value);
              }
            }}
          />

          <Typography>מתוך {totalPages}</Typography>
        </Box>

        <Button
          variant="outlined"
          disabled={currentPage >= totalPages}
          onClick={() => setCurrentPage(p => p + 1)}
        >
          הבא
        </Button>
      </Box>
    </>
  );
}
